import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import GrowthShell from '../../components/growth/GrowthShell';
import RangeBar from '../../components/growth/GrowthRange';
import ClaritySessions from '../../components/growth/ClaritySessions';
import { sessionsFor } from '../../lib/claritySessions';
import { BLOCK_AR, adminLink, daysAr, fetchProductLists, gapReasons, pct } from '../../lib/growthProducts';
import { MIN_SAMPLE, rangeLabel, rangeWindow, readRange, spanLabel, writeRange } from '../../lib/growthRange';
import { marketImageUrl } from '../../lib/marketCatalog';

// Growth OS «المنتجات» — the five product-health lists (report section C).
// Numbers come from growth_product_daily (nightly, Riyadh days); data-quality
// flags are the product's current state. Below MIN_SAMPLE a rate is greyed
// with «بيانات قليلة». Thresholds are starting values, shown on the page.

const LOW = 'بيانات قليلة';
const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const short = (t) => { const s = (t || '').trim(); return s.length > 60 ? `${s.slice(0, 58)}…` : s || '—'; };

function Rate({ value, base, unit = '%' }) {
  if (value == null) return <span className="gos-faint">—</span>;
  const low = base < MIN_SAMPLE;
  return (
    <span className={low ? 'gos-rate low' : 'gos-rate'} title={low ? `${LOW}: ${fmt(base)} فقط (الحد ${MIN_SAMPLE})` : undefined} data-low-sample={low ? '1' : '0'}>
      <bdi dir="ltr">{value}{unit}</bdi>{low && <small> · {LOW}</small>}
    </span>
  );
}

function Row({ r, reason, nums, win }) {
  const admin = adminLink(r);
  return (
    <div className="gos-prow" data-product={r.product_id}>
      <div className="gos-pimg">{r.image_ref ? <img src={marketImageUrl(r.image_ref, true)} alt="" loading="lazy" /> : null}</div>
      <div className="gos-pbody">
        <p className="gos-ptitle" title={r.title_ar}>{short(r.title_ar)}</p>
        <p className="gos-preason">{reason}</p>
        <div className="gos-pnums">{nums}</div>
        {win && <ClaritySessions label="جلسات شاهدته" load={() => sessionsFor('product_viewed', { key: 'product_id', value: r.product_id, from: win.start, to: win.end })} />}
      </div>
      <div className="gos-plinks">
        <a className="gos-link" href={`/market/${r.product_id}`} target="_blank" rel="noopener noreferrer">صفحة المتجر ↗</a>
        {admin && <a className="gos-link" href={admin.href} target={admin.internal ? undefined : '_blank'} rel={admin.internal ? undefined : 'noopener noreferrer'}>{admin.label}{admin.internal ? '' : ' ↗'}</a>}
      </div>
    </div>
  );
}

function List({ id, title, rule, list, empty, children, note }) {
  const rows = list?.rows || [];
  return (
    <section className="gos-card gos-plist" data-list={id}>
      <div className="gos-card-head">
        <h3>{title}</h3>
        {list && <span className="gos-pcount" data-total={list.total}>{fmt(list.total)} منتج</span>}
      </div>
      <p className="gos-card-sub">{rule}</p>
      {note}
      {!list ? <div className="gos-empty-state">…</div> : rows.length === 0 ? (
        <div className="gos-empty-state"><strong>لا توجد بيانات بعد</strong>{empty}</div>
      ) : <div className="gos-prows">{rows.map(children)}</div>}
    </section>
  );
}

const N = ({ k, v }) => <span><b>{v}</b> {k}</span>;

export default function GrowthProducts({ user, profile, lang }) {
  const [params, setParams] = useSearchParams();
  const range = useMemo(() => readRange(params), [params]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null); setErr(false);
    fetchProductLists(range).then((d) => { if (alive) setData(d); })
      .catch((e) => { console.warn('[GrowthProducts] load failed:', e?.message || e); if (alive) setErr(true); });
    return () => { alive = false; };
  }, [range.from, range.to]); // eslint-disable-line react-hooks/exhaustive-deps

  const win = rangeWindow(range);
  const t = data?.thresholds || { min_sample: MIN_SAMPLE, min_viewers: 30, atc_rate: 0.02, remove_rate: 0.4, active_days: 30 };
  const thinTracking = data && data.tracked_days < t.active_days;
  const refreshed = data?.refreshed_at ? new Date(data.refreshed_at).toLocaleString('ar-SA-u-ca-gregory-nu-latn', { timeZone: 'Asia/Riyadh', dateStyle: 'medium', timeStyle: 'short' }) : null;

  return (
    <GrowthShell user={user} profile={profile} lang={lang} active="products" title="المنتجات"
                 actions={<span className="gos-top-range">{spanLabel(range.from, range.to)}</span>}>
      <div className="gos-hello">
        <h2>صحة المنتجات</h2>
        <p>
          {rangeLabel(range)}. الأرقام تُحسب كل ليلة بتوقيت الرياض{refreshed ? ` (آخر تحديث: ${refreshed})` : ''}، فبيانات اليوم تظهر صباح الغد.
          حالة البيانات (الوزن، البحري، الحجب) هي حالة المنتج الآن.
        </p>
      </div>
      <RangeBar range={range} onChange={(next) => setParams(writeRange(next))} compare={false} />

      <div className="gos-thresholds" data-thresholds="1">
        <b>القيم المبدئية:</b>
        <span>«تنشاف وما تنطلب»: {t.min_viewers} مشاهداً فأكثر، والإضافة للسلة أقل من <bdi dir="ltr">{Math.round(t.atc_rate * 100)}%</bdi></span>
        <span>«تنضاف وتنشال»: تُزال من <bdi dir="ltr">{Math.round(t.remove_rate * 100)}%</bdi> من السلال فأكثر</span>
        <span>«ما أحد فتحها»: نشط {t.active_days} يوماً فأكثر بلا مشاهدة</span>
        <span>أقل من {t.min_sample}: النسبة باهتة («{LOW}»)</span>
      </div>

      {err && <div className="gos-card" style={{ marginBottom: 16 }}><div className="gos-empty-state"><strong>تعذّر تحميل القوائم</strong>أعد تحميل الصفحة.</div></div>}

      <div className="gos-plists">
        <List id="gaps" title="ناقصة البيانات وتنفتح" list={data?.gaps}
              rule="منتجات فيها نقص يمنع حساب الشحن أو يشكّك فيه، ويفتحها الناس. مرتبة بالمشاهدات."
              empty="ستظهر هنا حين تُفتح منتجات بياناتها ناقصة.">
          {(r) => <Row key={r.product_id} r={r} win={win} reason={gapReasons(r).join(' · ')}
                       nums={<><N k="مشاهدة" v={fmt(r.views)} /><N k="مشاهد" v={fmt(r.viewers)} /><N k="غادروا دون إضافة" v={fmt(r.left_no_add)} /></>} />}
        </List>

        <List id="unwanted" title="تنشاف وما تنطلب" list={data?.unwanted}
              rule={`كثيرة المشاهدة وقليلة الإضافة للسلة، مع آخر جزء رآه الزائر قبل أن يغادر. من 5 مشاهدين تظهر هنا باهتة، ومن ${t.min_viewers} تُعدّ.`}
              empty="ستظهر حين تتجمّع مشاهدات كافية.">
          {(r) => <Row key={r.product_id} r={r} win={win}
                       reason={`أكثر ما رآه قبل المغادرة: ${BLOCK_AR[r.top_block] || r.top_block || '—'}`}
                       nums={<><N k="مشاهد" v={fmt(r.viewers)} /><span>إضافة للسلة <Rate value={pct(r.add_sessions, r.viewers)} base={r.viewers} /></span></>} />}
        </List>

        <List id="removed" title="تنضاف وتنشال" list={data?.removed}
              rule={<>تُزال من <bdi dir="ltr">{Math.round(t.remove_rate * 100)}%</bdi> أو أكثر من السلال التي أُضيفت إليها. من 3 سلال تظهر هنا باهتة، ومن {t.min_sample} تُعدّ.</>}
              empty="ستظهر حين تتجمّع إضافات للسلة.">
          {(r) => <Row key={r.product_id} r={r} win={win} reason="أُضيف للسلة ثم أُزيل"
                       nums={<><N k="سلة أُضيف إليها" v={fmt(r.add_sessions)} /><span>أُزيل من <Rate value={pct(r.remove_sessions, r.add_sessions)} base={r.add_sessions} /></span></>} />}
        </List>

        <List id="unopened" title="ما أحد فتحها" list={data?.unopened}
              rule={`نشطة منذ ${t.active_days} يوماً أو أكثر ولم تُفتح مرة في هذه الفترة. مرتبة بمبيعاتها في المصدر.`}
              empty={`لا يوجد منتج نشط منذ ${t.active_days} يوماً بعد، أو كل المنتجات فُتحت.`}
              note={thinTracking && (
                <p className="gos-note low" data-low-sample="1">{LOW}: التتبّع يغطي {daysAr(data.tracked_days)} فقط من هذه الفترة، فـ«لم تُفتح» قد تعني فقط أنها لم تُفتح منذ بدأ التتبّع.</p>
              )}>
          {(r) => <Row key={r.product_id} r={r}
                       reason={`نشط منذ ${daysAr(Math.floor((Date.now() - Date.parse(r.created_at)) / 864e5))} بلا مشاهدة`}
                       nums={r.sold_lifetime != null ? <N k="مبيعات في المصدر" v={fmt(r.sold_lifetime)} /> : null} />}
        </List>

        <List id="sold" title="تنباع" list={data?.sold}
              rule="طلبات مدفوعة في الفترة (من سجل الطلبات الفعلي، دون طلبات حسابات الفريق)، مع عدد المشاهدات لكل بيعة."
              empty="لا طلبات مدفوعة في هذه الفترة.">
          {(r) => <Row key={r.product_id} r={r} win={win} reason="بِيع في هذه الفترة"
                       nums={<>
                         <N k="طلب" v={fmt(r.paid_orders)} /><N k="قطعة" v={fmt(r.units)} />
                         <N k="ر.س" v={fmt(Math.round(r.revenue_sar))} />
                         <span>مشاهدات لكل بيعة <Rate value={r.views ? Math.round((r.views / r.paid_orders) * 10) / 10 : null} base={r.paid_orders} unit="" /></span>
                       </>} />}
        </List>
      </div>
    </GrowthShell>
  );
}
